import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UserModel } from '../models';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly api        = inject(ApiService);
  private readonly USER_KEY   = 'cb_userInfo';

  private readonly _user = signal<UserModel | null>(this.loadFromStorage());

  readonly user  = this._user.asReadonly();
  readonly email = computed(() => this._user()?.email ?? null);

  loadCurrentUser(userId: number): Observable<void> {
    return this.api.get<UserModel>(`users/${userId}`).pipe(
      tap(user => this.set(user)),
      map(() => void 0),
    );
  }

  set(user: UserModel): void {
    this._user.set(user);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
  }

  clear(): void {
    this._user.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.USER_KEY);
    }
  }

  private loadFromStorage(): UserModel | null {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return null;
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      return raw ? (JSON.parse(raw) as UserModel) : null;
    } catch {
      return null;
    }
  }
}
